/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { chatDrawerActions } from 'ducks/chatDrawer'
import { memo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { CLIENT_SIDE_TOOLS } from '../tools/clientSideTools'
import { callFunction } from '../tools/dispatcher'
import ControlTray from './ControlTray'
import { useGeminiLiveAPIContext } from './GeminiLiveAPIContext'
import { ToolCall } from './lib/multimodal-live-types'

// These are hard-coded for now, they will eventually be pulled from the server, including fresh content.
import { authSelectors } from 'ducks/auth'
import { SERVER_SIDE_TOOLS } from '../tools/serverSideToolsDummy'
import instructionsDummy from './instructionsDummy'

interface PropertyDefinition {
  type: string | string[]
  [key: string]: any
}

interface Parameters {
  properties: {
    [key: string]: PropertyDefinition
  }
  [key: string]: any
}

const convertToGoogleLifeApiFunctionCallDefinition = (tool: any) => {
  // Exclude "type" property
  const { type, parameters, ...rest } = tool
  let toolUpdated = {
    ...rest,
    parameters: {
      ...parameters,

      // Exclude parameters.additionalProperties and parameters.strict
      additionalProperties: undefined,
      strict: undefined,
    },
  }

  delete toolUpdated.parameters.additionalProperties
  delete toolUpdated.parameters.strict

  // replace type: ['string','null'] with type: 'string'
  // This is required because Google Live API expects a single type but allows null.
  // Whereas, to use strict in OpenAI all fields are required.

  Object.entries(toolUpdated.parameters.properties as Record<string, PropertyDefinition>).forEach(([key, value]) => {
    // remove the 'null' value and use the first value from the array as the whole value
    // e.g. type: ['string', 'null'] -> type: 'string'
    if (Array.isArray(value.type) && value.type.includes('null')) {
      value.type = value.type.filter((t: string) => t !== 'null')[0]
      value.nullable = true
    }
  })

  return toolUpdated
}

// Beware: Gemini is extremely sensitive to incorrect function call definitions which will crash the stream immediately.
const declarations: any[] = [
  // {
  //   // type: 'function',
  //   name: 'navigate',
  //   description: 'Navigate to a specific page in the app. Populate either pathname or url, and leave the other null.',
  //   parameters: {
  //     type: 'object',
  //     properties: {
  //       pathname: { type: 'string', description: 'The pathname of the page to navigate to' },
  //       url: { type: 'string', description: 'The url of the page to navigate to' },
  //     },
  //     required: ['pathname', 'url'],
  //     // additionalProperties: false,
  //     // strict: true,
  //   },
  // },
  ...CLIENT_SIDE_TOOLS.map(convertToGoogleLifeApiFunctionCallDefinition),
  ...SERVER_SIDE_TOOLS.map(convertToGoogleLifeApiFunctionCallDefinition),
]

function ComponentComponent() {
  const { client, setConfig } = useGeminiLiveAPIContext()
  const history = useHistory()
  const dispatch = useDispatch()
  const orgId = useSelector(authSelectors.getOrgId)

  useEffect(() => {
    setConfig({
      model: 'models/gemini-2.0-flash-exp',
      generationConfig: {
        responseModalities: 'audio',
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Aoede' } },
        },
      },
      systemInstruction: {
        parts: [
          {
            text: instructionsDummy,
          },
        ],
      },
      tools: [{ functionDeclarations: declarations }],
    })
  }, [setConfig])

  useEffect(() => {
    const onToolCall = async (toolCall: ToolCall) => {
      console.log(`got toolcall`, toolCall)
      const functionResponses: any[] = []

      for (let i = 0; i < toolCall.functionCalls.length; i++) {
        const fc = toolCall.functionCalls[i]
        const output = await callFunction(fc, history, orgId)

        functionResponses.push({
          response: output,
          id: fc.id,
        })
      }

      // send data for the response of your tool call
      if (toolCall.functionCalls.length) {
        functionResponses.forEach((fr) => {
          if (fr.message) {
            dispatch(chatDrawerActions.addMessage(fr.message, 'receive'))
          }
        })

        setTimeout(() => {
          client.sendToolResponse({
            functionResponses,
          })
        }, 200)
      }
    }
    client.on('toolcall', onToolCall)
    return () => {
      client.off('toolcall', onToolCall)
    }
  }, [client, history])

  return <ControlTray />
}

export const Component = memo(ComponentComponent)
